import { Transition } from "@headlessui/react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Flaticon from "../../components/Flaticon/Flaticon";
import Modality from "../../components/Modality/Modality";
import * as session from "../../modules/Stash/Stash";
import { clsx } from "../../modules/Utilkit/Utilkit";
import { useAuth } from "./Auth";
import { useTitleBarTracker } from "./TitleBarTracker";


import Logo from "../../assets/images/logo.png";
import { Contexture, ContextureButton, ContexturePanel, ContextureSwitch } from "../../components/Contexture/Contexture";

/* NAVBAR */
const Navbar = ({ nav, usernav }) => {
  const { isOverlayVisible } = useTitleBarTracker();
  const { handleSignOut } = useAuth();

  const [ showSignOutModal, setShowSignOutModal ] = useState(false);

  return (
    <div className={ clsx(
      "flex items-center select-none min-h-full h-fit w-[env(titlebar-area-width,100%)] duration-150 pl-1",
      isOverlayVisible && "pr-4"
    ) } >
      <SignOutModal
        show={ showSignOutModal }
        onClose={ () => setShowSignOutModal(false) }
        onSignOut={ () => {
          handleSignOut();
          setShowSignOutModal(false);
        } }
      />

      <NavbarLogo />

      <QuickAccessPanel />

      <div className="flex space-x-2 px-2 items-center">
        <AppsMenu nav={ nav } />

        <UserMenu usernav={ usernav } onSignOut={ () => setShowSignOutModal(true) } />
      </div>

    </div>
  );
};

/* SIGN OUT MODAL */
const SignOutModal = ({ show, onClose, onSignOut }) => {
  return (
    <Modality
      show={ show }
      onClose={ onClose }
      label="Conferma uscita"
      icon={ { name: "leave", type: "sr", className: "text-red-500" } }
      buttons={ [
        { name: "Annulla", onClick: onClose },
        { name: "Esci", onClick: onSignOut, styleSet: 'error' }
      ] }
    >
      Sei sicuro di voler uscire?
    </Modality>
  );
};

/* NAVBAR LOGO */
const NavbarLogo = () => {
  return (
    <div className={ clsx(
      "overflow-hidden flex pr-1",
      "h-[calc(env(titlebar-area-height,33px)*0.7)] [-webkit-app-region:drag]"
    ) }>
      <img className="select-none w-6" draggable={ false } src={ Logo } alt="PDP Logo" />
      <span className="font-semibold px-1">PDP</span>
    </div>
  );
};

/* QUICK ACCESS BUTTON */
const QuickAccessButton = ({ item, index }) => {
  const { name, onClick, icon, loading = false, disabled = false, badge, tooltip } = item;
  const [ isLoading, setIsLoading ] = useState(loading);

  const handleClick = useCallback(async () => {
    if (disabled || isLoading) return;
    
    setIsLoading(true);
    try {
      await onClick?.();
    } finally {
      setIsLoading(false);
    }
  }, [onClick, disabled, isLoading]);

  if (!icon || !icon.name || !icon.type) {
    console.warn(`QuickAccessButton: missing icon configuration for "${name}"`);
    return null;
  }

  return (
    <button
      key={ `qap-button-${index}` }
      className={ clsx(
        "relative flex items-center justify-center px-1.5 space-x-1 rounded-md text-gray-800 text-nowrap transition-all",
        disabled || isLoading 
          ? "opacity-50 cursor-not-allowed" 
          : "hover:bg-gray-200 active:scale-95"
      ) }
      onClick={ handleClick }
      disabled={ disabled || isLoading }
      title={ tooltip || name }
      aria-label={ name }
      aria-busy={ isLoading }
    >
      <Flaticon 
        name={ isLoading ? "spinner" : icon.name } 
        type={ icon.type } 
        size={ "calc(env(titlebar-area-height,33px)*0.4)" }
        className={ isLoading ? "animate-spin" : "" }
      />
      <span className="ml-2 text-[calc(env(titlebar-area-height,33px)*0.45)]">{ name }</span>
      
      { badge !== undefined && badge > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
          { badge > 99 ? '99+' : badge }
        </span>
      ) }
    </button>
  );
};

/* QUICK ACCESS SEARCH */
const QuickAccessSearch = ({ item, index }) => {
  const { name, onInput, icon, debounce = 300, clearable = true, value: controlledValue, minChars = 0 } = item;
  const [ searchValue, setSearchValue ] = useState(controlledValue || "");
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    if (controlledValue !== undefined) {
      setSearchValue(controlledValue);
    }
  }, [controlledValue]);

  const handleInput = useCallback((event) => {
    const value = event.target.value;
    setSearchValue(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (value.length >= minChars) {
        onInput?.(value);
      }
    }, debounce);
  }, [onInput, debounce, minChars]);

  const handleClear = useCallback(() => {
    setSearchValue("");
    onInput?.("");
  }, [onInput]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  if (!icon || !icon.name || !icon.type) {
    console.warn(`QuickAccessSearch: missing icon configuration for "${name}"`);
    return null;
  }

  return (
    <div className="relative flex" key={ `qap-search-${index}` }>
      <Flaticon
        className="absolute top-1/2 -translate-y-1/2 left-1.5 text-gray-400 pointer-events-none"
        name={ icon.name }
        type={ icon.type }
        size={ 14 }
      />
      <input
        type="text"
        value={ searchValue }
        onChange={ handleInput }
        className="pl-7 pr-7 py-0.5 rounded-md border border-transparent focus:border-blue-300 focus:outline-none focus:ring-0 text-sm"
        placeholder={ name }
        aria-label={ name }
        minLength={ minChars }
      />
      { clearable && searchValue.length > 0 && (
        <button
          onClick={ handleClear }
          className="absolute top-1/2 -translate-y-1/2 right-1.5 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Cancella ricerca"
        >
          <Flaticon name="cross-small" type="rr" size={ 14 } />
        </button>
      ) }
    </div>
  );
};

/* QUICK ACCESS PANEL */
const QuickAccessPanel = () => {
  const { showQuickAccessPanel, quickAccessPanel } = useNavigator();
  const { isOverlayVisible } = useTitleBarTracker();

  const renderedItems = useMemo(() => {
    return quickAccessPanel.map((item, index) => {
      const { type = 'button' } = item;

      switch (type) {
        case 'button':
          return <QuickAccessButton key={ `qap-${index}` } item={ item } index={ index } />;
        case 'search':
          return <QuickAccessSearch key={ `qap-${index}` } item={ item } index={ index } />;
        case 'separator':
          return <div key={ `qap-sep-${index}` } className="w-px h-4 bg-gray-300" />;
        default:
          console.warn(`QuickAccessPanel: unknown type "${type}"`);
          return null;
      }
    });
  }, [quickAccessPanel]);

  return (
    <div className="flex-1 flex items-center">
      <Transition
        show={ showQuickAccessPanel }
        as="div"
        className="flex space-x-2 overflow-hidden"
        enter="transition ease-out duration-300"
        enterFrom="opacity-0 translate-x-10"
        enterTo="opacity-100 translate-x-0"
        leave="transition ease-in duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        { renderedItems }
      </Transition>

      { isOverlayVisible && (
        <div className="flex-1 w-full [-webkit-app-region:drag] h-[env(titlebar-area-height,33px)] top-0 left-0 z-50" />
      ) }
    </div>
  );
};

/* APPS MENU */
const AppsMenu = ({ nav }) => {
  const { active, go, setShowQuickAccessPanel } = useNavigator();

  return (
    <Contexture>
      <ContextureSwitch className="flex justify-center items-center rounded-full hover:bg-gray-200 duration-100 z-50 w-6 h-6">
        <Flaticon name="apps" type="sr" className="text-gray-600 " size={ 18 } />
      </ContextureSwitch>

      <ContexturePanel
        id="apps-menu"
        className={ clsx(
          "flex flex-col space-y-1 p-1 rounded-sm overflow-hidden",
          "bg-white shadow-lg focus:outline-none border border-gray-300"
        ) }
      >
        { ({ onClose }) => {
          return nav.map(({ id, name, description, icon, image, className }) => (
            <ContextureButton
              key={ id }
              as="button"
              className={ clsx(
                "flex items-center space-x-1 px-1 rounded-sm whitespace-nowrap select-none hover:bg-gray-200",
                active === id && "bg-gray-100"
              ) }
              onClick={ () => {
                if (id === active) return;
                setShowQuickAccessPanel(false);
                go(id);
                onClose();
              } }
            >
              <div className="w-10 h-10 flex justify-center items-center rounded-sm">
                { image ? (
                  <img src={ image } className="w-[32px]" alt={ name } />
                ) : (
                  <Flaticon name={ icon.name } type={ icon.type } size={ 20 } />
                ) }
              </div>

              <div className="flex flex-col items-start justify-center">
                <span className="font-semibold">{ name }</span>
                <span>{ description }</span>
              </div>
            </ContextureButton>

          ));
        } }
      </ContexturePanel>
    </Contexture>
  );
};

/* USER MENU */
const UserMenu = ({ usernav, onSignOut }) => {
  const { userData: { fullname, initials, username } } = useAuth();
  const softwareVersion = process.env.REACT_APP_VERSION ?? 'dev';

  return (
    <Contexture>
      <ContextureSwitch className="rounded-full bg-gray-300 border border-red-500 duration-100 flex justify-center items-center w-6 h-6">
        <div className="rounded-full flex justify-center items-center text-center font-medium text-sm w-full h-full text-[14px]">
          { initials }
        </div>
      </ContextureSwitch>

      <ContexturePanel
        id="user-menu"
        className={ clsx(
          "w-[180px] flex flex-col p-1 focus:outline-none border border-gray-300",
          "rounded-sm bg-white shadow-lg overflow-hidden"
        ) }
      >
        { ({ onClose }) => {
          return (
            <>
              <div className="absolute top-0 left-0 w-full h-7 bg-red-200" />

              <div className="flex z-10 flex-col justify-center items-center">
                <div className="w-full flex flex-col justify-center items-center mb-1">
                  <div className="w-12 h-12 mb-2 flex justify-center items-center shadow-md bg-color rounded-full text-xl font-semibold bg-gray-300 text-gray-800 border border-red-500">
                    { initials }
                  </div>
                </div>

                <div className="bg-gray-200 px-1 rounded-md">
                  { username }
                </div>

                <div>
                  { fullname }
                </div>

                <div className="text-xs font-thin text-gray-400">
                  Software Version { softwareVersion }
                </div>

                <div className="flex flex-col w-full border-t pt-1 mt-2">
                  { usernav.map(({ id, name, className, onClick }) => (
                    <button
                      key={ id }
                      className={ clsx(
                        "flex w-full justify-center rounded-sm items-center",
                        "p-1.5 duration-150 hover:bg-gray-100 space-x-2 px-2"
                      ) }
                      onClick={ () => {
                        if (id === 'logout') {
                          onSignOut();
                        } else {
                          onClick();
                        }
                        onClose();
                      } }
                    >
                      <div className={ className }>{ name }</div>
                    </button>
                  )) }
                </div>
              </div>
            </>
          );
        } }
      </ContexturePanel>
    </Contexture>
  );
};

const NavigatorContext = createContext();

const NavigatorProvider = ({ children }) => {
  const [ active, setActive ] = useState(session.getActive() || 'dashboard');
  const [ params, setParams ] = useState(session.getParams() || {});
  const [ showQuickAccessPanel, setShowQuickAccessPanel ] = useState(false);
  const [ quickAccessPanel, setQuickAccessPanel ] = useState([]);
  const transitionTimeoutRef = useRef(null);

  const go = useCallback((_active, _params = {}) => {
    if (_active !== active) {
      session.setSession({ active: _active, params: _params });
      setActive(_active);
      setParams(_params);
    }
  }, [active]);

  const updateQuickAccessPanel = useCallback((items, transitionDelay = 350) => {
    if (!Array.isArray(items)) {
      console.error('updateQuickAccessPanel: items must be an array');
      return;
    }

    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }

    if (showQuickAccessPanel) {
      setShowQuickAccessPanel(false);
    }

    transitionTimeoutRef.current = setTimeout(() => {
      setQuickAccessPanel(items);
      setShowQuickAccessPanel(true);
    }, transitionDelay);
  }, [showQuickAccessPanel]);

  const updateQuickAccessItem = useCallback((index, updates) => {
    setQuickAccessPanel(prev => {
      const newPanel = [...prev];
      if (newPanel[index]) {
        newPanel[index] = { ...newPanel[index], ...updates };
      }
      return newPanel;
    });
  }, []);

  const removeQuickAccessItem = useCallback((index) => {
    setQuickAccessPanel(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearQuickAccessPanel = useCallback(() => {
    setShowQuickAccessPanel(false);
    setTimeout(() => setQuickAccessPanel([]), 350);
  }, []);

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, []);

  const value = useMemo(() => ({
    active,
    params,
    go,
    showQuickAccessPanel,
    setShowQuickAccessPanel,
    quickAccessPanel,
    setQuickAccessPanel: updateQuickAccessPanel,
    updateQuickAccessItem,
    removeQuickAccessItem,
    clearQuickAccessPanel
  }), [
    active, 
    params, 
    go, 
    showQuickAccessPanel, 
    quickAccessPanel, 
    updateQuickAccessPanel,
    updateQuickAccessItem,
    removeQuickAccessItem,
    clearQuickAccessPanel
  ]);

  return (
    <NavigatorContext.Provider value={ value }>
      { children }
    </NavigatorContext.Provider>
  );
};

const useNavigator = () => useContext(NavigatorContext);

export default Navbar;
export { NavigatorProvider, useNavigator };

